
import { supabase } from "../integrations/supabase/client";

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  body: string;
  data: {
    type: string;
    related_entity_id?: string | null;
    related_entity_type?: string | null;
    sender_name?: string | null;
    pet_name?: string | null;
    post_content?: string | null;
  };
  read: boolean;
  created_at: string;
}

interface NotificationRequest {
  recipient_user_id: string;
  content: string;
  type: 'comment' | 'like' | 'follow' | 'system';
  send_push?: boolean;
  related_entity_id?: string;
  related_entity_type?: string;
}

// Helper para obtener títulos según tipo de notificación
const getNotificationTitle = (type: string): string => {
  const titles: Record<string, string> = {
    'match_request': 'New Match Request',
    'match_accepted': 'Match Accepted',
    'match_rejected': 'Match Declined',
    'like': 'New Like',
    'comment': 'New Comment',
    'follow': 'New Follower',
    'system': 'System Notification',
    'feedback_response': 'Feedback Response',
    'content_warning': 'Content Warning'
  };
  return titles[type] || 'Notification';
};

export const fetchNotifications = async (userId: string): Promise<Notification[]> => {
  try {
    console.log("Fetching notifications for user:", userId);
    
    // ✅ Consultar directamente la tabla con RLS (más eficiente)
    const { data, error } = await supabase
      .from('notifications')
      .select(`
        *,
        actor:users!actor_user_id(full_name, avatar_url)
      `)
      .eq('recipient_user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error fetching notifications:", error);
      throw new Error(`Error fetching notifications: ${error.message}`);
    }

    // Transformar al formato esperado por la UI
    const formattedNotifications: Notification[] = (data || []).map((n: any) => ({
      id: n.id,
      user_id: n.recipient_user_id,
      title: getNotificationTitle(n.type),
      body: n.content_preview || '',
      data: {
        type: n.type,
        related_entity_id: n.related_entity_id,
        related_entity_type: n.related_entity_type,
        sender_name: n.actor?.full_name || null,
        pet_name: null, // Se puede obtener por separado si es necesario
        post_content: null
      },
      read: n.is_read,
      created_at: n.created_at
    }));

    console.log(`✅ Fetched ${formattedNotifications.length} notifications directly from DB`);
    return formattedNotifications;
  } catch (error: any) {
    console.error("Error in fetchNotifications:", error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId: string): Promise<boolean> => {
  try {
    console.log("Marking notification as read:", notificationId);
    
    // ✅ Usar RPC function directamente (más eficiente que edge function)
    const { error } = await supabase.rpc('mark_notification_as_read', {
      p_notification_id: notificationId
    });

    if (error) {
      console.error("Error marking notification as read:", error);
      throw new Error(`Error marking notification as read: ${error.message}`);
    }

    return true;
  } catch (error: any) {
    console.error("Error in markNotificationAsRead:", error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async (userId: string): Promise<boolean> => {
  try {
    console.log("Marking all notifications as read for user:", userId);
    
    // ✅ Usar RPC function directamente (más eficiente que edge function)
    const { error } = await supabase.rpc('mark_all_notifications_as_read', {
      p_user_id: userId
    });

    if (error) {
      console.error("Error marking all notifications as read:", error);
      throw new Error(`Error marking all notifications as read: ${error.message}`);
    }

    return true;
  } catch (error: any) {
    console.error("Error in markAllNotificationsAsRead:", error);
    throw error;
  }
};

export const requestPushPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn("This browser does not support push notifications");
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return false;
  }
};

export const registerPushToken = async (userId: string, token: string, platform: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('register-token', {
      body: {
        user_id: userId,
        token: token,
        platform: platform,
        updated_at: new Date().toISOString()
      },
    });

    if (error) {
      console.error("Error registering push token:", error);
      return false;
    }

    return data?.success || false;
  } catch (error: any) {
    console.error("Error in registerPushToken:", error);
    return false;
  }
};

export const deleteNotification = async (notificationId: string, userId: string): Promise<boolean> => {
  try {
    // ✅ Usar RPC function directamente (más eficiente que edge function)
    const { data, error } = await supabase.rpc('delete_notification', {
      p_notification_id: notificationId,
      p_user_id: userId
    });

    if (error) {
      console.error('Error deleting notification:', error);
      throw new Error(`Failed to delete notification: ${error.message}`);
    }

    return data || false;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

export const deleteSelectedNotifications = async (notificationIds: string[], userId: string): Promise<boolean> => {
  try {
    // ✅ Usar RPC function directamente (más eficiente que edge function)
    const { data, error } = await supabase.rpc('delete_notifications', {
      p_notification_ids: notificationIds,
      p_user_id: userId
    });

    if (error) {
      console.error('Error deleting selected notifications:', error);
      throw new Error(`Failed to delete selected notifications: ${error.message}`);
    }

    return data || false;
  } catch (error) {
    console.error('Error deleting selected notifications:', error);
    throw error;
  }
};

export const deleteAllNotifications = async (userId: string): Promise<boolean> => {
  try {
    // ✅ Usar RPC function directamente (más eficiente que edge function)
    const { data, error } = await supabase.rpc('delete_all_notifications', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error deleting all notifications:', error);
      throw new Error(`Failed to delete all notifications: ${error.message}`);
    }

    return data || false;
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
};

export const triggerNotification = async (request: NotificationRequest): Promise<{ success: boolean; message?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('send-notification', {
      body: request,
    });

    if (error) {
      console.error("Error triggering notification:", error);
      return { success: false, message: error.message };
    }

    return data || { success: true };
  } catch (error: any) {
    console.error("Error in triggerNotification:", error);
    return { success: false, message: error.message };
  }
};
